import { ApiUrl } from 'api/client'
import { Command } from 'common/Prose/Commands'
import { schema } from 'prose-core'
import { TextSelection } from 'prosemirror-state'
import { docToMarkdown } from 'proseShared/plugins/PredictAI/docToMarkdown'
import { PredictAIPluginKey, PredictAIPluginMeta, PredictAIStatus } from 'proseShared/plugins/PredictAI/PredictAI'
import store from 'redux/store'

export enum PredictionType {
  'completion',
  'insertion',
}

type requestBody = {
  model: string
  messages: Array<{
    role: 'system' | 'user'
    content: string
  }>
  max_tokens?: number
  n?: number
  best_of?: number
  stop?: [string] | [string, string] | [string, string, string] | [string, string, string, string]
  logit_bias?: Record<string, number>
  stream?: boolean
} & ({ temperature?: number } | { top_p?: number })

export enum PredictCommandMode {
  'manual',
  'automatic',
}

export const predictAI =
  (mode: PredictCommandMode): Command =>
  (state, dispatch, view) => {
    if (!view?.editable) return false
    let { selection, tr } = state
    if (
      !selection ||
      !(selection instanceof TextSelection) ||
      !selection.empty || // not cursor
      selection.$anchor.parent.type !== schema.nodes.paragraph || // not in paragraph
      selection.$anchor.parentOffset + 2 < selection.$anchor.parent.nodeSize // not at end of paragraph
    ) {
      return false
    }
    const preparedPayload = docToMarkdown(tr)
    tr = state.tr
    if (!PredictAIPluginKey.get(state)) {
      // plugin not initialized (probably missing feature flag)
      return false
    }

    let meta: PredictAIPluginMeta = {
      action: 'start',
      position: selection.anchor,
    }

    const { headers: stateHeaders } = store.getState()

    dispatch?.(tr.setMeta(PredictAIPluginKey, meta))
    ;(async () => {
      const openAIResponse = await fetch(`${ApiUrl}/ai-completions`, {
        method: 'POST',
        headers: {
          ...stateHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: `I have typed this entire document, and then marked a special position indicated by >|<. Suggest what should be typed at this position, as though you were autocompleting. You can use the entire doc as context, but the suggestions must connect to the sentence before and after >|< (as applicable).`,
            },
            { role: 'user', content: `\`\`\`markdown\n` + preparedPayload + `\n\`\`\`` },
            {
              role: 'system',
              content: `Do not include a preamble or explanation, nor a copy of the surrounding text, nor >|<, as I am unable to differentiate the suggestion from your thoughs, and thus will treat your entire response as the suggestion regardless of what it contains. I will replace >|< with whatever you reply, so you may need to begin your reply with a space or newline, or adjust the case of the first letter.`,
            },
          ],
          max_tokens: 64,
          stream: true,
        } as requestBody),
      })
      if (!openAIResponse.body) return
      const reader = openAIResponse.body.getReader()
      const decoder = new TextDecoder('utf-8')
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)

        const lines = chunk.split('\n')

        const contentPiece = lines.flatMap((line) => {
          line = line.replace(/^data: /, '').trim()
          if (line === '') {
            return []
          }
          if (line === '[DONE]') {
            return [null]
          }
          const data: { choices: Array<{ delta: { content: string } }> } = JSON.parse(line)
          const content = data.choices[0].delta.content
          if (content) {
            return [content]
          }
          return []
        })

        document.dispatchEvent(new CustomEvent('chunkReceived', { detail: contentPiece }))
      }
    })()

    return true
  }

export const maybeApplyAIPrediction: Command = (state, dispatch) => {
  const { tr } = state
  const pluginState = PredictAIPluginKey.getState(state)
  if (pluginState && pluginState.status === PredictAIStatus.done && pluginState.prediction) {
    tr.insertText(pluginState.prediction)
    tr.setMeta(PredictAIPluginKey, { status: PredictAIStatus.idle })
    dispatch?.(tr)
    return true
  }
  return false
}

export const maybeRejectAIPrediction: Command = (state, dispatch) => {
  const { tr } = state
  const pluginState = PredictAIPluginKey.getState(state)
  if (pluginState && pluginState.status === PredictAIStatus.done && pluginState.prediction) {
    tr.setMeta(PredictAIPluginKey, { status: PredictAIStatus.idle })
    dispatch?.(tr)
    return true
  }
  return false
}
